"use client";

import React, { useState } from "react";
import { useDarkMode } from '@/lib/DarkModeContext';
import { useSiteLanguage } from '@/lib/SiteLanguageContext';

export default function NewsletterSubscribeWidget() {
    const { darkMode } = useDarkMode();
    const { siteLang } = useSiteLanguage();
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [message, setMessage] = useState('');

    const handleSubscribe = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email || !email.includes('@')) {
            setStatus('error');
            setMessage(siteLang === 'en' ? 'Please enter a valid email address' : 'Molimo unesite validnu e-mail adresu');
            return;
        }

        setStatus('loading');
        setMessage('');

        try {
            const response = await fetch('/api/newsletter/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();

            if (!response.ok) {
                setStatus('error');
                setMessage(data.error || (siteLang === 'en' ? 'An error occurred. Please try again.' : 'Došlo je do greške. Pokušajte ponovo.'));
                return;
            }

            setStatus('success');
            setMessage(data.message || (siteLang === 'en' ? 'Successfully subscribed to the newsletter!' : 'Uspešno ste se prijavili na newsletter!'));
            setEmail('');

            // Reset success message after 5 seconds
            setTimeout(() => {
                setStatus('idle');
                setMessage('');
            }, 5000);
        } catch (error) {
            setStatus('error');
            setMessage(siteLang === 'en' ? 'Connection error. Please try again.' : 'Greška pri povezivanju. Pokušajte ponovo.');
            console.error('Newsletter subscription error:', error);
        }
    };

    return (
        <div className={`rounded-lg p-6 md:p-8 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-blue-50 border border-blue-200'}`}>
            {/* Header */}
            <div className="mb-6">
                <h3 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    📧 {siteLang === 'en' ? 'Studies of Defense Systems Newsletter' : 'Studije Odbrambenih Sistema Newsletter'}
                </h3>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {siteLang === 'en'
                        ? 'Expert analyses of defense systems with an analytical approach and technical precision.'
                        : 'Stručne analize odbrambenih sistema sa analitičkim pristupom i tehničkom preciznosću.'}
                </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubscribe} className="space-y-3">
                <div>
                    <label htmlFor="email-widget" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {siteLang === 'en' ? 'Enter your email address' : 'Unesite vašu e-mail adresu'}
                    </label>
                    <input
                        type="email"
                        id="email-widget"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="vas@email.com"
                        className={`w-full px-4 py-2 rounded-lg border-2 transition-colors text-sm ${darkMode
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none'
                            }`}
                        disabled={status === 'loading'}
                    />
                    <p className={`text-xs mt-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        📧 {siteLang === 'en'
                            ? 'Emails may end up in your spam folder. Mark as not spam so future emails go directly to your inbox.'
                            : 'Emails mogu biti u mapi za neželjnu poštu. Označite da nije neželjina (spam) kako bi ih u budućnosti primali direktno u inbox.'}
                    </p>
                </div>

                <button
                    type="submit"
                    disabled={status === 'loading'}
                    className={`w-full px-4 py-2 rounded-lg font-semibold transition-all text-sm ${status === 'loading'
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                        }`}
                >
                    {status === 'loading' ? (siteLang === 'en' ? 'Subscribing...' : 'Prijava u toku...') : (siteLang === 'en' ? 'Subscribe' : 'Prijavi se')}
                </button>

                {/* Status Messages */}
                {message && (
                    <div
                        className={`p-3 rounded-lg text-sm font-medium ${status === 'success'
                            ? darkMode
                                ? 'bg-green-900 text-green-200'
                                : 'bg-green-100 text-green-800'
                            : darkMode
                                ? 'bg-red-900 text-red-200'
                                : 'bg-red-100 text-red-800'
                            }`}
                    >
                        {message}
                    </div>
                )}
            </form>
        </div>
    );
}
